// src/components/Customers/CustomersShadcn.jsx - Using GenericTableShadcn
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Search, Filter, Mail, Phone, DollarSign } from 'lucide-react';

// Import shadcn components
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Alert, AlertDescription } from "../ui/alert";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Skeleton } from "../ui/skeleton";

// Import our custom components
import GenericTableShadcn from '../ui/table/GenericTableShadcn';
import CustomerCreationModal from './CustomerCreationModal';
import PageContainer from '../ui/layout/PageContainer/PageContainer';

// Import hook
import { useCustomers } from '../../hooks/useCustomers';

const CustomersShadcn = () => {
  const navigate = useNavigate();

  // Get data from hook
  const {
    customers,
    customersLoading,
    customersError,
    deleteCustomer,
    clearCustomersError,
    searchCustomers,
    isConnected
  } = useCustomers();

  // Local UI state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter customers based on search and status
  const filteredCustomers = useMemo(() => {
    if (!customers) return [];

    return customers.filter(customer => {
      // Search filtering
      const matchesSearch = !searchTerm || [
        customer.name,
        customer.full_name,
        customer.customer_name,
        customer.email,
        customer.email_address,
        customer.phone,
        customer.phone_number,
        customer.mobile
      ].some(field =>
        field && field.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Status filtering
      const matchesStatus = statusFilter === 'all' || (() => {
        const now = new Date();
        const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        const lastApptDate = customer.last_appointment_date || customer.lastAppointmentDate || customer.last_appointment;
        const createdDate = customer.created_at || customer.createdAt || customer.date_created;

        switch (statusFilter) {
          case 'active':
            return lastApptDate && new Date(lastApptDate) > ninetyDaysAgo;
          case 'inactive':
            return !lastApptDate || new Date(lastApptDate) <= ninetyDaysAgo;
          case 'new':
            return createdDate && new Date(createdDate) > thirtyDaysAgo;
          default:
            return true;
        }
      })();

      return matchesSearch && matchesStatus;
    });
  }, [customers, searchTerm, statusFilter]);

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(parseFloat(amount || 0));
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper function to get customer initials
  const getInitials = (name) => {
    if (!name) return 'UC';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Column definitions for customers
  const columns = [
    {
      key: 'customer',
      header: 'Customer',
      render: (customer) => {
        const customerData = {
          id: customer.id || customer.customer_id || customer._id,
          name: customer.name || customer.full_name || customer.customer_name || 'Unknown Customer',
        };
        const createdDate = customer.created_at || customer.createdAt || customer.date_created;
        const customerSince = createdDate ? new Date(createdDate).toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric'
        }) : 'Unknown';

        return (
          <div className="flex items-center space-x-3">
            <Avatar>
              <AvatarFallback className="bg-gradient-to-br from-emerald-500/20 to-blue-500/20 text-emerald-400">
                {getInitials(customerData.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground truncate">
                {customerData.name}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                Customer since {customerSince}
              </p>
            </div>
          </div>
        );
      },
      skeleton: (
        <div className="flex items-center space-x-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-[150px]" />
            <Skeleton className="h-3 w-[120px]" />
          </div>
        </div>
      )
    },
    {
      key: 'contact',
      header: 'Contact Info',
      render: (customer) => {
        const email = customer.email || customer.email_address || '';
        const phone = customer.phone || customer.phone_number || customer.mobile || '';
        return (
          <div className="min-w-0">
            {email && (
              <p className="font-medium text-foreground truncate flex items-center">
                <Mail className="w-3 h-3 mr-2 text-muted-foreground flex-shrink-0" />
                {email}
              </p>
            )}
            {phone && (
              <p className="text-sm text-muted-foreground truncate flex items-center">
                <Phone className="w-3 h-3 mr-2 text-muted-foreground flex-shrink-0" />
                {phone}
              </p>
            )}
          </div>
        );
      },
      skeleton: (
        <div className="space-y-2">
          <Skeleton className="h-4 w-[120px]" />
          <Skeleton className="h-3 w-[100px]" />
        </div>
      )
    },
    {
      key: 'activity',
      header: 'Activity',
      render: (customer) => {
        const appointmentCount = customer.appointment_count || customer.appointmentCount || 0;
        const lastAppointment = customer.last_appointment_date || customer.lastAppointmentDate;
        return (
          <div className="min-w-0">
            <p className="font-medium text-foreground">
              {appointmentCount} appointments
            </p>
            <p className="text-sm text-muted-foreground">
              Last: {formatDate(lastAppointment)}
            </p>
          </div>
        );
      },
      skeleton: (
        <div className="space-y-2">
          <Skeleton className="h-4 w-[100px]" />
          <Skeleton className="h-3 w-[80px]" />
        </div>
      )
    },
    {
      key: 'value',
      header: 'Value',
      render: (customer) => {
        const totalSpent = customer.total_spent || customer.totalSpent || 0;
        return (
          <div className="flex items-center space-x-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-foreground">
              {formatCurrency(totalSpent)}
            </span>
          </div>
        );
      },
      skeleton: <Skeleton className="h-4 w-[80px]" />
    }
  ];

  // Event handlers
  const handleRowClick = (customer) => {
    const customerId = customer.id || customer.customer_id || customer._id;
    navigate(`/customers/${customerId}`);
  };

  const handleDelete = (customer) => {
    const customerId = customer.id || customer.customer_id || customer._id;
    deleteCustomer(customerId);
  };

  const getDeleteConfirmMessage = (customer) => {
    const customerName = customer.name || customer.full_name || customer.customer_name || 'this customer';
    return `Are you sure you want to delete ${customerName}? This action cannot be undone.`;
  };

  const handleSearch = (value) => {
    setSearchTerm(value);

    // Backend search for longer terms
    if (value.length >= 2) {
      const timeoutId = setTimeout(() => {
        searchCustomers(value);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  };

  // Empty state configuration
  const emptyState = {
    icon: Users,
    title: searchTerm || statusFilter !== 'all'
      ? 'No matching customers'
      : 'No customers yet',
    description: searchTerm || statusFilter !== 'all'
      ? 'Try adjusting your search or filter criteria.'
      : 'Get started by adding your first customer.',
    action: (!searchTerm && statusFilter === 'all') ? {
      icon: Plus,
      label: 'Create First Customer',
      onClick: () => setShowCreateModal(true)
    } : null
  };

  // Connection error
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertDescription>
            Not connected to server. Please check your connection and try again.
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>

      {/* Error Alert */}
      {customersError && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            {customersError}
            <Button
              variant="outline"
              size="sm"
              onClick={clearCustomersError}
              className="ml-2"
            >
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="flex space-x-4 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Customers</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="new">New</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Generic Table */}
      <GenericTableShadcn
        data={filteredCustomers}
        columns={columns}
        loading={customersLoading && customers.length === 0}
        emptyState={emptyState}
        onRowClick={handleRowClick}
        onDelete={handleDelete}
        deleteConfirmMessage={getDeleteConfirmMessage}
      />

      {/* Results Summary */}
      {filteredCustomers.length > 0 && (
        <div className="text-center text-muted-foreground text-sm mt-6">
          Showing {filteredCustomers.length} of {customers?.length || 0} customers
          {customersLoading && (
            <span className="ml-2">
              <span className="inline-block w-2 h-2 bg-primary rounded-full animate-pulse mr-1" />
              Updating...
            </span>
          )}
        </div>
      )}

      {/* Creation Modal */}
      <CustomerCreationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(customerData) => {
          console.log('Customer created successfully:', customerData.name);
          setShowCreateModal(false);
        }}
      />
    </PageContainer>
  );
};

export default CustomersShadcn;
